import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, apiError } from '../../lib/api';

export default function OwnerBoost() {
  const [plans, setPlans] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [selectedBiz, setSelectedBiz] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [p, b] = await Promise.all([api.get('/payments/plans'), api.get('/owner/businesses')]);
        setPlans(p.data.plans || []);
        setBusinesses(b.data.businesses || []);
        if ((b.data.businesses || []).length) setSelectedBiz(String(b.data.businesses[0].id));
      } catch (e) { setErr(apiError(e)); }
    })();
  }, []);

  const buy = async (planKey) => {
    if (!selectedBiz) { setErr('Please pick a business first.'); return; }
    setErr(''); setBusy(planKey);
    try {
      const { data } = await api.post('/owner/payments/checkout', {
        plan_key: planKey,
        business_id: Number(selectedBiz),
        origin_url: window.location.origin,
      });
      // Redirect to Stripe (or in simulator mode, straight to /payment/success)
      window.location.href = data.checkout_url;
    } catch (e) { setErr(apiError(e, 'Could not start checkout.')); setBusy(''); }
  };

  return (
    <div data-testid="owner-boost">
      <h2 className="mb-1">Boost my business</h2>
      <p className="text-muted-2">Promote your listing to the top of search results and category pages. Cancel anytime.</p>

      {err && <div className="alert alert-danger" data-testid="boost-err">{err}</div>}

      <div className="ap-card mb-3">
        <label className="form-label">Pick a business</label>
        {businesses.length === 0 ? (
          <div className="small text-muted-2">You don't have any businesses yet. <Link to="/owner/add">Add one first.</Link></div>
        ) : (
          <select className="form-select" value={selectedBiz} onChange={e => setSelectedBiz(e.target.value)} data-testid="boost-biz-select">
            {businesses.map(b => (
              <option key={b.id} value={b.id}>
                {b.name} — {b.city_name} {b.is_premium ? '(currently premium)' : ''}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="row g-3">
        {plans.map(p => {
          const isFeatured = p.key === 'premium';
          return (
            <div className="col-md-4" key={p.key}>
              <div
                className="ap-card h-100 d-flex flex-column"
                style={{
                  borderColor: isFeatured ? 'var(--ap-primary)' : undefined,
                  borderWidth: isFeatured ? 2 : 1,
                  position: 'relative',
                }}
                data-testid={`plan-${p.key}`}
              >
                {isFeatured && (
                  <span
                    className="badge rounded-pill"
                    style={{ position: 'absolute', top: -12, left: 20, background: 'var(--ap-primary)', color: '#fff', letterSpacing: '.15em', fontWeight: 700 }}
                  >MOST POPULAR</span>
                )}
                <div className="text-muted-2 small" style={{ textTransform: 'uppercase', letterSpacing: '.16em', fontWeight: 700 }}>{p.name}</div>
                <div className="mt-2" style={{ fontFamily: 'Bricolage Grotesque', fontSize: '2.2rem', fontWeight: 800 }}>
                  ₹ {p.amount}
                  <span className="text-muted-2" style={{ fontSize: '.9rem', fontWeight: 400, marginLeft: 6 }}>/ {p.duration_days} days</span>
                </div>
                <ul className="list-unstyled small mt-3 flex-grow-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="mb-2 d-flex align-items-start">
                      <span style={{ color: 'var(--ap-primary)', marginRight: 8, fontWeight: 700 }}>✓</span>{f}
                    </li>
                  ))}
                </ul>
                <button
                  className={isFeatured ? 'btn-ap w-100' : 'btn-ap-outline w-100'}
                  onClick={() => buy(p.key)}
                  disabled={!selectedBiz || busy === p.key}
                  data-testid={`buy-${p.key}`}
                >
                  {busy === p.key ? 'Redirecting…' : `Get ${p.name}`}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-center mt-4">
        <Link to="/owner/payments" className="btn-ap-outline" data-testid="owner-payment-history-link">View payment history →</Link>
      </div>
    </div>
  );
}
